import React, { useRef, useState } from "react";
import CollapsibleSection from "./CollapsibleSection";
import { formatCurrency } from "../../utils/helper";
import { uploadFile } from "../../utils/driveClient";
import {
  totalCost,
  owedToYou,
  alreadyPaid,
  owedAtClosing,
  addItem,
  updateItem,
  removeItem,
} from "../../utils/costReceiptTracker";

const PAID_BY_OPTIONS = [
  { value: "", label: "—" },
  { value: "you", label: "Agent" },
  { value: "seller", label: "Seller" },
  { value: "other", label: "Other" },
  { value: "closing", label: "Pay at Closing" },
];

// Shared grid template so the column headers line up with each row's cells:
// Done | Item | Vendor | Cost | Paid By | Reimbursed | Receipt | (remove)
const GRID =
  "grid grid-cols-[40px_minmax(120px,1fr)_minmax(90px,0.8fr)_84px_120px_92px_64px_24px] gap-2 items-center min-w-[720px]";

const onlyDigits = (s) => String(s).replace(/[^\d]/g, "");
const fmtInput = (v) =>
  v === "" || v == null ? "" : "$" + Number(v).toLocaleString("en-US");

export default function CostReceiptTracker({
  title,
  addLabel = "Add Item",
  items = [],
  onChange,
  deal,
  isEdit = true,
  showTotal = true,
  hideOwedWhenZero = false,
}) {
  const list = Array.isArray(items) ? items : [];
  const owed = owedToYou(list);
  const paid = alreadyPaid(list);
  const closing = owedAtClosing(list);
  const showOwed = !hideOwedWhenZero || owed > 0;
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputs = useRef({});

  const patch = (id, p) => onChange(updateItem(list, id, p));
  const add = () => onChange(addItem(list, String(Date.now())));
  const remove = (id) => onChange(removeItem(list, id));

  const handleReceipt = async (id, file) => {
    if (!file || !deal?.driveFolderId) return;
    setUploadingId(id);
    try {
      const f = await uploadFile(deal.driveFolderId, file);
      patch(id, {
        receiptUrl: f?.webViewLink || "",
        receiptName: f?.name || file.name,
        receiptFileId: f?.id || "",
      });
    } catch (e) {
      console.error("Receipt upload failed:", e);
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <CollapsibleSection title={title}>
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        {showOwed && (
          <div className="flex items-center justify-end px-3 py-2 bg-slate-50">
            <span className="text-xs font-semibold bg-amber-100 text-amber-800 rounded-full px-3 py-1">
              Owed to Agent: {formatCurrency(owed)}
            </span>
          </div>
        )}

        {list.length === 0 ? (
          <p className="text-sm text-slate-400 px-3 py-3">No items yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <div
              className={`${GRID} px-3 py-2 bg-slate-100 text-[10px] font-semibold uppercase tracking-wide text-slate-500`}
            >
              <span>Done</span>
              <span>Item</span>
              <span>Vendor</span>
              <span>Cost</span>
              <span>Paid By</span>
              <span>Reimbursed</span>
              <span>Receipt</span>
              <span></span>
            </div>
            {list.map((it) => {
              const paidByAgent = it.paidBy === "you";
              return (
                <div
                  key={it.id}
                  className={`${GRID} px-3 py-2 border-t border-slate-100`}
                >
                  <input
                    type="checkbox"
                    checked={!!it.done}
                    onChange={(e) => patch(it.id, { done: e.target.checked })}
                    className="size-4 justify-self-center"
                    disabled={!isEdit}
                  />
                  <input
                    type="text"
                    value={it.description}
                    placeholder="Describe the item…"
                    onChange={(e) => patch(it.id, { description: e.target.value })}
                    className={`text-sm rounded border border-slate-300 px-2 py-1 w-full ${
                      it.done ? "line-through text-slate-400" : ""
                    }`}
                    disabled={!isEdit}
                  />
                  <input
                    type="text"
                    value={it.vendor || ""}
                    placeholder="Vendor"
                    onChange={(e) => patch(it.id, { vendor: e.target.value })}
                    className="text-sm rounded border border-slate-300 px-2 py-1 w-full"
                    disabled={!isEdit}
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    value={fmtInput(it.cost)}
                    placeholder="$0"
                    onChange={(e) => patch(it.id, { cost: onlyDigits(e.target.value) })}
                    className="text-sm rounded border border-slate-300 px-2 py-1 w-full"
                    disabled={!isEdit}
                  />
                  <select
                    value={it.paidBy || ""}
                    onChange={(e) => patch(it.id, { paidBy: e.target.value })}
                    className="text-sm rounded border border-slate-300 px-2 py-1 w-full"
                    disabled={!isEdit}
                  >
                    {PAID_BY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="checkbox"
                    checked={!!it.reimbursed}
                    onChange={(e) => patch(it.id, { reimbursed: e.target.checked })}
                    disabled={!isEdit || !paidByAgent}
                    className="size-4 justify-self-center"
                    title={
                      paidByAgent ? "" : "Only applies when the agent paid"
                    }
                  />
                  <div className="text-xs">
                    {it.receiptUrl ? (
                      <a
                        href={it.receiptUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="font-semibold text-sky-600"
                      >
                        📎 View
                      </a>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => fileInputs.current[it.id]?.click()}
                          disabled={!isEdit || !deal?.driveFolderId || uploadingId === it.id}
                          className="font-semibold text-sky-600 disabled:text-slate-300"
                          title={
                            !deal?.driveFolderId
                              ? "No Drive folder on this deal yet"
                              : ""
                          }
                        >
                          {uploadingId === it.id ? "…" : "＋ Add"}
                        </button>
                        <input
                          ref={(el) => (fileInputs.current[it.id] = el)}
                          type="file"
                          className="hidden"
                          onChange={(e) => handleReceipt(it.id, e.target.files?.[0])}
                        />
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(it.id)}
                    disabled={!isEdit}
                    className="text-slate-300 hover:text-red-500 text-sm justify-self-center"
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="px-3 py-2 border-t border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <button
            type="button"
            onClick={add}
            disabled={!isEdit}
            className="text-sm font-semibold text-sky-600"
          >
            ＋ {addLabel}
          </button>
          {showTotal && (
            <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap justify-end">
              <span>
                Already Paid:{" "}
                <b className="text-slate-800">{formatCurrency(paid)}</b>
              </span>
              <span>
                Owed at Closing:{" "}
                <b className="text-amber-700">{formatCurrency(closing)}</b>
              </span>
              <span>
                Total:{" "}
                <b className="text-slate-800">{formatCurrency(totalCost(list))}</b>
              </span>
            </div>
          )}
        </div>
      </div>
    </CollapsibleSection>
  );
}
